import { Radio, type RadioChangeEvent, type RadioGroupProps } from 'antd';
import clx from 'classnames';
import { omit } from 'lodash';
import { useCallback } from 'react';
import styled from 'styled-components';
import { useInputController } from '../hooks/useInputController.ts';
import { IsRadioOrCheckboxGroupElementSymbol } from '../item/symbols/isRadioOrCheckboxGroupElement.tsx';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility.tsx';
import { useFormItemEnabled } from '../item/useFormItemEnabled.ts';

const StyledRadioGroup = styled(Radio.Group)`
  flex-flow: wrap;
  &.vertical {
    display: flex;
    flex-direction: column;
    > label {
      width: max-content;
      max-width: 100%;
    }
  }
  &.read-only {
    pointer-events: none;
  }
`;

export interface IRadioGroupInputProps
  extends Omit<RadioGroupProps, 'name' | 'onChange'> {
  name: string;

  /**
   * Permette lo sviluppo in verticale delle opzioni
   * -> un opzione per ogni riga
   */
  vertical?: boolean; // TODO: da implementare
  /**
   * Aggungiamo proprietà readOnly non gestita da antd
   */
  readOnly?: boolean;
  /**
   * Permette di personalizzare la onChange del Radio.
   * Utilizzare `onChange` per richiamare la onChange del Controller
   */
  onChange?: (value: any, onChange: (value: any) => void) => void;
  /**
   * in caso che dobbiamo trasformare il valore per l'input
   */
  transformValue?: (value: any) => any;
}

export function RadioGroupInput(props: IRadioGroupInputProps) {
  const {
    name,
    vertical,
    readOnly,
    className,
    onChange: onCustomChange,
    transformValue,
    ...inputProps
  } = props;

  const { enabled, visuallyDisabled } = useFormItemEnabled();

  const {
    field: { value, onChange, onBlur, ref },
  } = useInputController(name, { enabled });
  const transformedValue = transformValue?.(value) ?? value;

  const handleChange = useCallback(
    (e: RadioChangeEvent) => {
      onCustomChange
        ? onCustomChange(e.target.value, onChange)
        : onChange(e.target.value);
    },
    [onChange, onCustomChange],
  );

  const accessibility = useFormItemAccessibility();

  return (
    <div
      role="radiogroup"
      {...omit(accessibility.attributes, 'id')}
      aria-orientation={vertical ? 'vertical' : 'horizontal'}
      aria-labelledby={accessibility.labelId}
    >
      <StyledRadioGroup
        size="large"
        disabled={visuallyDisabled}
        {...inputProps}
        value={transformedValue}
        onBlur={onBlur}
        ref={ref}
        name={name}
        onChange={readOnly ? undefined : handleChange}
        className={clx(className, { vertical, 'read-only': readOnly })}
      />
    </div>
  );
}

RadioGroupInput[IsRadioOrCheckboxGroupElementSymbol] = true;
